import React, { useContext } from 'react';
import SupplierNotificationHeaderForm, { SupplierNotificationHeaderData } from '../forms/SupplierNotificationHeaderForm';
import useForm from '@hooks/useForm/useForm';
import { ApiContext } from '@api/api';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { SnPartyConfigDTO } from '@api/ringil-api.v2';
import useBEValidation from '@hooks/useBEValidation/useBEValidation';
import RingilSidebar from '@components/ringil3/Sidebar/RingilSidebar';
import useSidebarControl, { SidebarControlProps } from '@hooks/useSidebarControl/useSidebarControl.tsx';
import * as yup from 'yup';

interface ComponentProps {
  initialData?: SupplierNotificationHeaderData;
  supplierNotificationId: string;
  readonly configuration: SnPartyConfigDTO;
}

// Module scope on purpose: a component declared inside the hook is a new type on every render of the
// owning page, and React remounts it — losing the form and refetching. See useSidebarControl.
const EditSupplierNotificationHeaderSidebar: React.FC<ComponentProps & SidebarControlProps> = ({
  initialData,
  supplierNotificationId,
  configuration,
  visible,
  onHide,
  onComplete,
}) => {
  const { updateNotificationSupplierUpdateDataForLocationStatus } = useContext(ApiContext);
  const { BackendValidationComponent, setBeValidationErrors, clear } = useBEValidation();

  const form = useForm<SupplierNotificationHeaderData>(
    yup.object().shape({}),
    initialData,
    v => {
      updateNotificationSupplierUpdateDataForLocationStatus(
        supplierNotificationId,
        form.values,
        () => {
          onHide();
          onComplete();
        },
        null,
        { onValidationFailed: setBeValidationErrors },
      );
    },
    true,
    true,
  );

  const { tr } = useTranslationLgs();

  return (
    <RingilSidebar
      visible={visible}
      heading={tr(`EditSupplierNotificationHeaderSidebar.updateSupplierNotification`, `Update supplier notification`)}
      onBack={onHide}
      onContinue={() => form.validateAndSend()}
    >
      <BackendValidationComponent />
      <SupplierNotificationHeaderForm form={form} configuration={configuration} showTimeslots={false} />
    </RingilSidebar>
  );
};

export const useCreateEditSupplierNotificationHeaderSidebar = (onComplete: () => void) => {
  const { sidebarProps, mountKey, setVisible } = useSidebarControl(onComplete);
  return { EditSupplierNotificationHeaderSidebar, sidebarProps, mountKey, setVisible };
};
